import { Capacitor } from '@capacitor/core';
import type { ProductDataType } from '@am3nd-org/pointcraft-types';
import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite';

import { isOfflineDemoActiveAtBoot } from 'src/features/offline-demo/boot';

import type { BusinessV3Item } from 'src/sections/admin-page/ListView/Businesses/types';

import DexieService from './DexieService';
import SQLiteService from './SQLiteService';
import { withTimeout } from '../promise-timeout';
import {
  DiagnosticIODeferred,
  type DiagnosticAdmission,
  assertDiagnosticAdmission,
} from './diagnostic-admission';
import {
  InventoryStats,
  SystemConfigType,
  OfflineMutationRecord,
  DiagnosticLogRecordRow,
  DiagnosticLogPageQuery,
  NewOfflineMutationRecord,
  DiagnosticLogStorageStats,
  NewDiagnosticLogRecordRow,
  CompletedSaleReportOutboxRecord,
  CompletedSaleReportHistoryRecord,
  RestaurantOrderEnrichmentCacheRecord,
} from './types';

export type PlatformDatabaseScope = 'primary' | 'offline-demo';

const DATABASE_NAMES: Record<PlatformDatabaseScope, { web: string; native: string }> = {
  primary: { web: 'MyDatabase', native: 'mydb' },
  'offline-demo': { web: 'PointCraftOfflineDemo', native: 'pointcraft_offline_demo' },
};

// A plugin bridge round-trip normally completes in well under a second; a call
// exceeding these limits is wedged (stale native state, locked DB file) and must
// reject so boot gates can retry or fail visibly instead of hanging forever.
const TIMEOUTS = {
  BRIDGE_CALL: 15_000,
  SCHEMA_INIT: 120_000, // DDL + one-time inventory backfill (scales with catalog size)
} as const;

export function getPlatformDatabaseName(
  scope: PlatformDatabaseScope,
  platform: 'web' | 'native'
): string {
  return DATABASE_NAMES[scope][platform];
}

export function getDefaultPlatformDatabaseScope(): PlatformDatabaseScope {
  return isOfflineDemoActiveAtBoot() ? 'offline-demo' : 'primary';
}

class PlatformService {
  private static instances = new Map<PlatformDatabaseScope, PlatformService>();

  private service: SQLiteService | DexieService;

  private static initPromises = new Map<PlatformDatabaseScope, Promise<PlatformService>>();

  // Private constructor now accepts an initialized service instance
  private constructor(service: SQLiteService | DexieService) {
    this.service = service;
  }

  /**
   * Returns the singleton PlatformService after ensuring the database is initialized.
   */
  static async getInstance(
    scope: PlatformDatabaseScope = getDefaultPlatformDatabaseScope()
  ): Promise<PlatformService> {
    // 1️⃣ A ready instance? return it.
    const readyInstance = PlatformService.instances.get(scope);
    if (readyInstance) return readyInstance;

    // 2️⃣ Someone else is already initialising? wait for them.
    const pendingInstance = PlatformService.initPromises.get(scope);
    if (pendingInstance) return pendingInstance;

    // 3️⃣ We are the first – start init and save the promise.
    const initPromise = (async () => {
      try {
        const platform = Capacitor.getPlatform();
        let svc: SQLiteService | DexieService;

        if (platform === 'ios' || platform === 'android') {
          const sqliteConn = new SQLiteConnection(CapacitorSQLite);
          const name = getPlatformDatabaseName(scope, 'native');

          // Keep native map sane
          const ok = (
            await withTimeout(
              sqliteConn.checkConnectionsConsistency(),
              TIMEOUTS.BRIDGE_CALL,
              'SQLite checkConnectionsConsistency timed out'
            )
          ).result;
          if (!ok)
            await withTimeout(
              sqliteConn.closeAllConnections(),
              TIMEOUTS.BRIDGE_CALL,
              'SQLite closeAllConnections timed out'
            );

          const has = (
            await withTimeout(
              sqliteConn.isConnection(name, false),
              TIMEOUTS.BRIDGE_CALL,
              'SQLite isConnection timed out'
            )
          ).result;
          const db = has
            ? await withTimeout(
                sqliteConn.retrieveConnection(name, false),
                TIMEOUTS.BRIDGE_CALL,
                'SQLite retrieveConnection timed out'
              )
            : await withTimeout(
                sqliteConn.createConnection(name, false, 'no-encryption', 1, false),
                TIMEOUTS.BRIDGE_CALL,
                'SQLite createConnection timed out'
              );

          await withTimeout(db.open(), TIMEOUTS.BRIDGE_CALL, `SQLite open timed out for ${name}`);

          svc = new SQLiteService(sqliteConn, db, name);
          await withTimeout(
            svc.initializeDB(),
            TIMEOUTS.SCHEMA_INIT,
            'SQLite schema initialization timed out'
          );
        } else if (platform === 'web') {
          svc = new DexieService(undefined, getPlatformDatabaseName(scope, 'web'));
          await svc.initializeDB();
        } else {
          throw new Error('Unsupported platform');
        }

        const instance = new PlatformService(svc);
        PlatformService.instances.set(scope, instance);
        return instance;
      } catch (error) {
        // CRITICAL FIX: Clear failed promise so retry is possible
        PlatformService.initPromises.delete(scope);
        console.error('PlatformService initialization failed:', error);
        throw error;
      }
    })();
    PlatformService.initPromises.set(scope, initPromise);

    const instance = await initPromise;
    // Clear promise only on success (after await completes)
    PlatformService.initPromises.delete(scope);
    return instance;
  }

  /** Diagnostics may wait for boot-owned initialization, but never start it. */
  static async getForDiagnostics(admit: DiagnosticAdmission): Promise<PlatformService> {
    assertDiagnosticAdmission(admit);
    const scope = getDefaultPlatformDatabaseScope();
    const pending = this.instances.get(scope) ?? this.initPromises.get(scope);
    if (!pending) throw new DiagnosticIODeferred();
    const instance = await pending;
    assertDiagnosticAdmission(admit);
    return instance;
  }

  static async closeScope(scope: PlatformDatabaseScope): Promise<void> {
    const instance = PlatformService.instances.get(scope);
    if (!instance) return;
    await instance.closeConnection();
    PlatformService.instances.delete(scope);
    PlatformService.initPromises.delete(scope);
  }

  // ---------------------------------------------------------------------------
  // system_config delegation
  // ---------------------------------------------------------------------------

  public async closeConnection(): Promise<void> {
    await this.service.closeConnection();
  }

  public async updateOrCreateSystemConfig<T>(type: string, data: T): Promise<void> {
    await this.service.updateOrCreateSystemConfig(type, data);
  }

  public async getSystemConfig<T>(type: string): Promise<SystemConfigType<T> | null> {
    return this.service.getSystemConfig<T>(type);
  }

  public async getAllSystemConfigs<T>(): Promise<SystemConfigType<T>[]> {
    return this.service.getAllSystemConfigs();
  }

  public async deleteSystemConfig(type: string): Promise<void> {
    await this.service.deleteSystemConfig(type);
  }

  public getSystemConfigSnapshot<T>(type: string) {
    return this.service.getSystemConfigSnapshot<T>(type);
  }

  public listSystemConfigSnapshots<T>(prefix: string) {
    return this.service.listSystemConfigSnapshots<T>(prefix);
  }

  public deleteSystemConfigIfUnchanged(
    expected: import('./system-config-snapshot').SystemConfigSnapshot<unknown>,
    canDelete: () => void
  ) {
    return this.service.deleteSystemConfigIfUnchanged(expected, canDelete);
  }

  public async deleteAllSystemConfigs(): Promise<void> {
    await this.service.deleteAllSystemConfigs();
  }

  // ---------------------------------------------------------------------------
  // inventory table delegation
  // ---------------------------------------------------------------------------

  public async ensureInventoryTable(): Promise<void> {
    await this.service.ensureInventoryTable();
  }

  public async upsertInventoryItem(product: ProductDataType): Promise<void> {
    await this.service.upsertInventoryItem(product);
  }

  public async bulkUpsertInventory(products: ProductDataType[]): Promise<void> {
    await this.service.bulkUpsertInventory(products);
  }

  public async hardDeleteInventoryItem(productId: string): Promise<void> {
    await this.service.hardDeleteInventoryItem(productId);
  }

  public async queryInventoryPage(params: {
    page: number;
    pageSize: number;
    search?: string;
    category?: string;
    taxable?: boolean;
    ebtEligible?: boolean;
    soldByWeight?: boolean;
    variablePrice?: boolean;
    productType?: ProductDataType['type'];
    sortBy?: 'recent' | 'name';
  }): Promise<{ items: ProductDataType[]; total: number }> {
    return this.service.queryInventoryPage(params);
  }

  public async countInventory(): Promise<number> {
    return this.service.countInventory();
  }

  public async sumInventoryStock(productType?: ProductDataType['type']): Promise<number> {
    return this.service.sumInventoryStock(productType);
  }

  public async clearAllInventory(): Promise<void> {
    await this.service.clearAllInventory();
  }

  public async getInventoryById(productId: string): Promise<ProductDataType | null> {
    return this.service.getInventoryById(productId);
  }

  public async getInventoryByIdentifier(identifier: string): Promise<ProductDataType | null> {
    return this.service.getInventoryByIdentifier(identifier);
  }

  public async getInventoryByVariablePriceLookupCode(
    lookupCode: string
  ): Promise<ProductDataType | null> {
    return this.service.getInventoryByVariablePriceLookupCode(lookupCode);
  }

  // ---------------------------------------------------------------------------
  // business_inventory_stats delegation
  // ---------------------------------------------------------------------------

  public async getInventoryCategoryCounts(
    productType?: ProductDataType['type']
  ): Promise<Array<{ category: string; count: number }>> {
    return this.service.getInventoryCategoryCounts(productType);
  }

  public async recalculateInventoryStats(): Promise<InventoryStats> {
    return this.service.recalculateInventoryStats();
  }

  public async getInventoryStats(options?: {
    recalculateIfMissing?: boolean;
    forceRecalculate?: boolean;
  }): Promise<InventoryStats> {
    return this.service.getInventoryStats(options);
  }

  // ---------------------------------------------------------------------------
  // admin_businesses table delegation
  // ---------------------------------------------------------------------------

  public async bulkReplaceAdminBusinesses(businesses: BusinessV3Item[]): Promise<void> {
    await this.service.bulkReplaceAdminBusinesses(businesses);
  }

  public async getAllAdminBusinesses(): Promise<BusinessV3Item[]> {
    return this.service.getAllAdminBusinesses();
  }

  public async clearAdminBusinesses(): Promise<void> {
    await this.service.clearAdminBusinesses();
  }

  // ---------------------------------------------------------------------------
  // restaurant_order_enrichment_cache delegation
  // ---------------------------------------------------------------------------

  public async getRestaurantOrderEnrichmentCache(
    businessId: string,
    saleId: string
  ): Promise<RestaurantOrderEnrichmentCacheRecord | null> {
    return this.service.getRestaurantOrderEnrichmentCache(businessId, saleId);
  }

  public async upsertRestaurantOrderEnrichmentCache(
    record: RestaurantOrderEnrichmentCacheRecord
  ): Promise<void> {
    await this.service.upsertRestaurantOrderEnrichmentCache(record);
  }

  public async deleteExpiredRestaurantOrderEnrichmentCache(beforeIso: string): Promise<number> {
    return this.service.deleteExpiredRestaurantOrderEnrichmentCache(beforeIso);
  }

  public async clearRestaurantOrderEnrichmentCache(): Promise<void> {
    await this.service.clearRestaurantOrderEnrichmentCache();
  }

  // ---------------------------------------------------------------------------
  // completed_sale_report_outbox delegation
  // ---------------------------------------------------------------------------

  public async upsertCompletedSaleReportOutbox(
    record: CompletedSaleReportOutboxRecord
  ): Promise<void> {
    await this.service.upsertCompletedSaleReportOutbox(record);
  }

  public async getCompletedSaleReportOutbox(
    reportIdempotencyKey: string
  ): Promise<CompletedSaleReportOutboxRecord | null> {
    return this.service.getCompletedSaleReportOutbox(reportIdempotencyKey);
  }

  public async listCompletedSaleReportOutbox(
    businessId: string
  ): Promise<CompletedSaleReportOutboxRecord[]> {
    return this.service.listCompletedSaleReportOutbox(businessId);
  }

  public async deleteCompletedSaleReportOutbox(reportIdempotencyKey: string): Promise<void> {
    await this.service.deleteCompletedSaleReportOutbox(reportIdempotencyKey);
  }

  // ---------------------------------------------------------------------------
  // completed_sale_report_history delegation
  // ---------------------------------------------------------------------------

  public async getCompletedSaleReportHistory(
    reportIdempotencyKey: string
  ): Promise<CompletedSaleReportHistoryRecord | null> {
    return this.service.getCompletedSaleReportHistory(reportIdempotencyKey);
  }

  public async listCompletedSaleReportHistory(
    businessId: string
  ): Promise<CompletedSaleReportHistoryRecord[]> {
    return this.service.listCompletedSaleReportHistory(businessId);
  }

  /** Atomically records ownership transfer, removes the outbox row, and enforces retention. */
  public async completeCompletedSaleReportOutbox(
    record: CompletedSaleReportHistoryRecord
  ): Promise<void> {
    await this.service.completeCompletedSaleReportOutbox(record);
  }

  // ---------------------------------------------------------------------------
  // offline_mutation_outbox delegation
  // ---------------------------------------------------------------------------

  public async insertOfflineMutation(
    record: NewOfflineMutationRecord
  ): Promise<OfflineMutationRecord> {
    return this.service.insertOfflineMutation(record);
  }

  public async updateOfflineMutation(record: OfflineMutationRecord): Promise<void> {
    await this.service.updateOfflineMutation(record);
  }

  public async listOfflineMutations(businessId: string): Promise<OfflineMutationRecord[]> {
    return this.service.listOfflineMutations(businessId);
  }

  public async deleteOfflineMutation(mutationId: string): Promise<void> {
    await this.service.deleteOfflineMutation(mutationId);
  }

  // ---------------------------------------------------------------------------
  // diagnostic_log_records delegation
  // ---------------------------------------------------------------------------

  public async insertDiagnosticLogRecords(
    records: readonly NewDiagnosticLogRecordRow[],
    admit: DiagnosticAdmission
  ): Promise<void> {
    await this.service.insertDiagnosticLogRecords(records, admit);
  }

  public async readDiagnosticLogPage(
    query: DiagnosticLogPageQuery,
    admit: DiagnosticAdmission
  ): Promise<DiagnosticLogRecordRow[]> {
    return this.service.readDiagnosticLogPage(query, admit);
  }

  public async claimDiagnosticLogRecords(
    businessId: string,
    limit: number,
    maxBytes: number,
    admit: DiagnosticAdmission
  ): Promise<DiagnosticLogRecordRow[]> {
    return this.service.claimDiagnosticLogRecords(businessId, limit, maxBytes, admit);
  }

  public async deleteDiagnosticLogRecords(
    recordIds: readonly string[],
    admit: DiagnosticAdmission
  ): Promise<void> {
    await this.service.deleteDiagnosticLogRecords(recordIds, admit);
  }

  public async deleteDiagnosticLogRecordsOlderThan(
    occurredBeforeIso: string,
    admit: DiagnosticAdmission
  ): Promise<number> {
    return this.service.deleteDiagnosticLogRecordsOlderThan(occurredBeforeIso, admit);
  }

  public async deleteDiagnosticLogRecordsForOtherBusinesses(
    businessId: string,
    admit: DiagnosticAdmission
  ): Promise<number> {
    return this.service.deleteDiagnosticLogRecordsForOtherBusinesses(businessId, admit);
  }

  public async trimDiagnosticLogRecordsToBytes(
    maxBytes: number,
    admit: DiagnosticAdmission
  ): Promise<number> {
    return this.service.trimDiagnosticLogRecordsToBytes(maxBytes, admit);
  }

  public async getDiagnosticLogStorageStats(
    admit: DiagnosticAdmission
  ): Promise<DiagnosticLogStorageStats> {
    return this.service.getDiagnosticLogStorageStats(admit);
  }

  public async clearDiagnosticLogRecords(admit: DiagnosticAdmission): Promise<void> {
    await this.service.clearDiagnosticLogRecords(admit);
  }
}

export default PlatformService;
